import { Controller as BaseController } from "stimulus";
import Player from "@vimeo/player";
import FakePlayer from "./fake_player";

// How far past the furthest watched point a seek may land before it is pulled
// back. Matches Orientation::VideoProgressRecorder::SEEK_GRACE on the server,
// which is what actually enforces it.
const SEEK_GRACE_SECONDS = 5;

// The report that the video ended is the one that finishes it, so a failed one
// is sent again. A fetch that failed never reached the server, so the second
// try still earns the credit the first would have.
const END_REPORT_RETRIES = 2;
const END_REPORT_RETRY_DELAY_MS = 2000;

const NOT_CONFIRMED_MESSAGE = "We couldn't confirm the whole video played. Press Play to watch it again.";
const NO_CONNECTION_MESSAGE = "We couldn't reach the server. Check your connection, then reload this page.";

export class Controller extends BaseController {
  static targets = ["player", "toggle", "bar", "clock", "error", "notice"];

  static values = {
    videoId: String,
    progressUrl: String,
    startAt: Number,
    duration: Number,
    playbackRate: Number,
    reportInterval: Number,
    fake: Boolean,
  };

  connect() {
    this.furthest = this.startAtValue;
    this.lastSeconds = this.startAtValue;
    this.playing = false;
    this.loadErrorMessage = this.errorTarget.textContent.trim();
    this.player = this.buildPlayer();

    // Vimeo answers the seek below only once the video first plays, so waiting
    // for it left a returning viewer's clock at 0:00 until they pressed Play.
    this.render(this.startAtValue);

    this.player.on("play", () => this.onPlay());
    this.player.on("pause", (data) => this.onPause(data));
    this.player.on("ended", (data) => this.onEnded(data));
    this.player.on("timeupdate", (data) => this.onTimeUpdate(data));
    this.player.on("seeked", (data) => this.onSeeked(data));
    // Vimeo reports a refused call here as well as to whoever made it, with the
    // call's name attached, and the caller decides what that means. Only a
    // failure of the player itself says the video couldn't be loaded.
    this.player.on("error", (data) => {
      if (!data || !data.method) this.showError();
    });

    this.player
      .ready()
      .then(() => this.player.setPlaybackRate(this.playbackRateValue).catch(() => {}))
      .then(() => this.player.setCurrentTime(this.startAtValue))
      .catch(() => this.showError());

    this.onVisibilityChange = () => {
      if (document.visibilityState === "hidden" && this.playing) {
        this.report("hidden", this.lastSeconds, { beacon: true });
      }
    };
    this.onPageHide = () => {
      if (this.playing) this.report("hidden", this.lastSeconds, { beacon: true });
    };
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("pagehide", this.onPageHide);
  }

  disconnect() {
    clearInterval(this.timer);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("pagehide", this.onPageHide);
    if (this.player && this.player.destroy) this.player.destroy().catch(() => {});
  }

  buildPlayer() {
    const options = {
      id: Number(this.videoIdValue),
      controls: false,
      keyboard: false,
      playsinline: true,
      responsive: true,
      dnt: true,
      // Without this Vimeo refuses setPlaybackRate, whatever the rate.
      speed: true,
    };

    // The stand-in gets the same options, so it refuses the same way.
    if (this.fakeValue) return new FakePlayer(this.playerTarget, { ...options, duration: this.durationValue });

    return new Player(this.playerTarget, options);
  }

  toggle() {
    if (this.playing) {
      this.player.pause();
    } else {
      this.player.play().catch(() => this.showError());
    }
  }

  fullscreen() {
    if (this.player.requestFullscreen) this.player.requestFullscreen().catch(() => {});
  }

  onPlay() {
    this.playing = true;
    this.toggleTarget.textContent = "Pause";
    this.hideMessages();
    this.report("play", this.lastSeconds);
    clearInterval(this.timer);
    this.timer = setInterval(() => this.report("progress", this.lastSeconds), this.reportIntervalValue);
  }

  onPause(data) {
    this.playing = false;
    this.toggleTarget.textContent = "Play";
    clearInterval(this.timer);
    this.report("pause", data.seconds);
  }

  onEnded(data) {
    this.playing = false;
    clearInterval(this.timer);
    this.toggleTarget.textContent = "Play";
    const seconds = data && data.seconds ? data.seconds : this.durationValue;

    this.reportEnded(seconds).then((result) => {
      if (result === null) {
        this.showError(NO_CONNECTION_MESSAGE);
      } else if (result.completed) {
        window.location.reload();
      } else {
        this.startOver();
      }
    });
  }

  // Resolves with the server's answer, or with null once the retries are used up.
  reportEnded(seconds, retriesLeft = END_REPORT_RETRIES) {
    return this.report("ended", seconds).then((result) => {
      if (result !== null || retriesLeft === 0) return result;

      return new Promise((resolve) => setTimeout(resolve, END_REPORT_RETRY_DELAY_MS)).then(() =>
        this.reportEnded(seconds, retriesLeft - 1),
      );
    });
  }

  // The video ended, but the server doesn't count the whole of it as watched,
  // and at its end there is nothing more to earn. So the viewer goes back to
  // the start, and the position the next report carries goes with them: that
  // report starts the credit again from 0. The furthest point stays where the
  // server has it, which is the end.
  startOver() {
    this.lastSeconds = 0;
    this.render(0);
    this.showError(NOT_CONFIRMED_MESSAGE);
    this.player.setCurrentTime(0).catch(() => {});
  }

  onTimeUpdate(data) {
    if (data.seconds > this.furthest + SEEK_GRACE_SECONDS) {
      this.player.setCurrentTime(this.furthest);
      return;
    }
    this.lastSeconds = data.seconds;
    this.furthest = Math.max(this.furthest, data.seconds);
    this.render(data.seconds);
  }

  onSeeked(data) {
    if (data.seconds > this.furthest + SEEK_GRACE_SECONDS) this.player.setCurrentTime(this.furthest);
  }

  render(seconds) {
    const percent = this.durationValue > 0 ? Math.min(100, (seconds / this.durationValue) * 100) : 0;
    this.barTarget.style.width = `${percent}%`;
    this.clockTarget.textContent = `${formatTime(seconds)} / ${formatTime(this.durationValue)}`;
  }

  showError(message = this.loadErrorMessage) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  // Playing again answers whatever was said about the viewing before it.
  hideMessages() {
    this.errorTarget.classList.add("hidden");
    if (this.hasNoticeTarget) this.noticeTarget.classList.add("hidden");
  }

  report(event, seconds, { beacon = false } = {}) {
    const position = Number(seconds || 0).toFixed(2);
    const tokenTag = document.querySelector('meta[name="csrf-token"]');
    const token = tokenTag ? tokenTag.content : "";

    // A beacon survives the page closing but cannot send headers, so its CSRF
    // token travels in the body, where Rails also looks for it.
    if (beacon && navigator.sendBeacon) {
      const body = new FormData();
      body.append("event", event);
      body.append("position", position);
      body.append("authenticity_token", token);
      navigator.sendBeacon(this.progressUrlValue, body);
      return Promise.resolve(null);
    }

    return fetch(this.progressUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ event, position }),
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => {
        if (json) this.furthest = Math.max(this.furthest, json.furthest_position_seconds);
        return json;
      })
      .catch(() => null);
  }
}

function formatTime(totalSeconds) {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = String(seconds % 60).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}` : `${minutes}:${rest}`;
}
